import { type InputHTMLAttributes, useContext } from "react";
import PasswordStrengthBar, { type PasswordStrengthBarProps } from "react-password-strength-bar";

import type { SupportedLocaleType } from "../../common/types/languageTypes";
import { FormContext } from "../formvalidation/FormContext";
import { validatePassword } from "../formvalidation/FormUtils/FieldValidators";
import ValidatedInput from "../formvalidation/ValidatedInput";

type PasswordFieldProps = InputHTMLAttributes<HTMLInputElement> & {
   label?: string;
   name: string;
   validator?: (value: string) => string;
   hasStrengthBar?: boolean;
   locale?: SupportedLocaleType;
};

type StrengthBarPropsByLocale = {
   [L in SupportedLocaleType]: Omit<PasswordStrengthBarProps, "password">;
};

const strengthBarPropsByLocale: StrengthBarPropsByLocale = {
   "nb-NO": {
      scoreWords: ["veldig svakt", "svakt", "ok", "bra", "sterkt"],
      shortScoreWord: ["for kort"]
   },
   "en-US": {}
};

const PasswordField = ({
   label = "Nytt passord",
   name,
   validator,
   hasStrengthBar = true,
   locale = "nb-NO"
}: PasswordFieldProps) => {
   const strengthBarProps = strengthBarPropsByLocale[locale];
   const { formValues } = useContext(FormContext);
   const passwordValue = formValues ? formValues[name]?.value : "";

   const allValidators = (value: string) => {
      const validatedPassword = validatePassword(value);
      if (validatedPassword.length > 0) {
         return validatedPassword;
      }
      if (validator) {
         return validator(value);
      }
      return "";
   };

   return (
      <div className="tw-pb-2">
         <ValidatedInput name={name} type="password" label={label} initialValue="" validator={allValidators} required />
         {hasStrengthBar && (
            <div className="tw-pt-1">
               <PasswordStrengthBar {...strengthBarProps} password={passwordValue} minLength={8} />
            </div>
         )}
      </div>
   );
};

export default PasswordField;
